import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { formatMinutes, localDateOf, shiftMinutes } from '@kronos/domain'
import type { Instant } from '@kronos/domain'
import { repository } from '../../data'
import { clockTime, problemOf, useNow } from '../hours/time'
import { ForgottenShift } from './ForgottenShift'

/**
 * The clock: one big button, and nothing to fill in.
 *
 * Start shift when you arrive, End shift when you leave. It never asks what
 * you are working on — that is recorded anyway, by the steps you sign off.
 * Lunch is just End and Start again; two shifts in a day add up the same.
 */
export function ClockCard(): JSX.Element {
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState('')
  const now = useNow()
  const mine = useQuery({ queryKey: ['shifts', 'mine', null], queryFn: () => repository.myShifts() })

  const done = (): void => {
    setProblem('')
    void queryClient.invalidateQueries({ queryKey: ['shifts'] })
    void queryClient.invalidateQueries({ queryKey: ['hours'] })
  }
  const clockIn = useMutation({ mutationFn: () => repository.clockIn(), onSuccess: done, onError: (e) => setProblem(problemOf(e)) })
  const clockOut = useMutation({ mutationFn: () => repository.clockOut(), onSuccess: done, onError: (e) => setProblem(problemOf(e)) })
  const busy = clockIn.isPending || clockOut.isPending

  if (mine.isPending) return <section className="clock-card" aria-busy="true" />
  if (mine.isError) return <section className="clock-card critical">{problemOf(mine.error)}</section>

  const { open, openForgotten, person, timeZone } = mine.data
  if (open && openForgotten) return <ForgottenShift shift={open} onFixed={done} />

  const nowIso = new Date(now).toISOString() as Instant
  const today = person?.days.find((d) => d.date === localDateOf(nowIso, timeZone))
  // Today's finished shifts from the report, plus the running one counted to this second.
  const todayMinutes =
    (today?.shifts.filter((s) => s.endedAt !== null).reduce((sum, s) => sum + s.minutes, 0) ?? 0) +
    (open ? shiftMinutes(open, nowIso) : 0)

  return (
    <section className={`clock-card${open ? ' clock-card--on' : ''}`} aria-label="Shift clock">
      {open ? (
        <>
          <div className="clock-card__row">
            <span className="stack" style={{ gap: 2 }}>
              <span className="clock-card__status">On shift since {clockTime(open.startedAt)}</span>
              <span className="clock-card__time">{formatMinutes(todayMinutes)}</span>
            </span>
            <span className="muted">{formatMinutes(person?.minutes ?? 0)} this pay period</span>
          </div>
          {/* Red: it ends something. */}
          <button className="clock-card__button danger" disabled={busy} onClick={() => clockOut.mutate()}>
            End shift
          </button>
        </>
      ) : (
        <>
          <span className="stack" style={{ gap: 2 }}>
            <span className="muted">{todayMinutes > 0 ? `${formatMinutes(todayMinutes)} so far today` : 'You’re off the clock'}</span>
            <span>Start your shift when you’re at the workshop.</span>
          </span>
          <button className="clock-card__button primary" disabled={busy} onClick={() => clockIn.mutate()}>
            Start shift
          </button>
        </>
      )}
      {problem ? (
        <p className="critical" role="alert">
          {problem}
        </p>
      ) : null}
    </section>
  )
}
